import { format } from 'date-fns'
import { Check, Plus, Image } from 'lucide-react'
import type { TattooTimelineEntry } from '@/types/models'
import { useImageLoadAndVisible } from '@/hooks/useImageLoadAndVisible'

interface TattooCardProps {
  tattoo: TattooTimelineEntry
  onAddHealed?: () => void
  onCardClick?: () => void
  isArtist?: boolean
  hideDate?: boolean
  onImageLoad?: () => void
}

export function TattooCard({ tattoo, onAddHealed, onCardClick, isArtist, onImageLoad }: TattooCardProps) {
  const hasHealed = tattoo.healedPhotos.length > 0
  const mainImage = tattoo.freshPhotos[0]?.url || '/placeholder-tattoo.png'

  // Use the reusable hook for image load and visibility detection
  const { imageLoaded, imgRef, handleImageLoad } = useImageLoadAndVisible({
    onImageLoadAndVisible: onImageLoad
  })

  return (
    <div className="flex flex-col items-center flex-shrink-0">
      {/* Card with image */}
      <div 
        className="relative rounded-[var(--radius)] overflow-hidden bg-card border border-border shadow-sm cursor-pointer hover:shadow-md transition-shadow mb-3"
        style={{ width: '160px', height: '200px' }}
        onClick={onCardClick}
      >
        {/* Main image */}
        <img
          ref={imgRef}
          src={mainImage}
          alt={tattoo.description || 'Tattoo'}
          className={`w-full h-full object-cover transition-opacity duration-500 ease-out ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={handleImageLoad}
        />
        
        {/* Loading skeleton - stays visible until image loads */}
        <div className={`absolute inset-0 bg-muted transition-opacity duration-300 ${
          imageLoaded ? 'opacity-0 pointer-events-none' : 'opacity-100 animate-pulse'
        }`} />

        {/* Status badge */}
        <div className="absolute top-2 right-2">
          {hasHealed ? (
            <div className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1">
              <Check className="w-3 h-3" />
              Healed
            </div>
          ) : (
            <div className="bg-primary text-white text-xs px-2 py-1 rounded-full font-medium">
              Fresh
            </div>
          )}
        </div>

        {/* Photo count indicator - Top Left */}
        <div className="absolute top-2 left-2 bg-background/90 backdrop-blur-sm rounded-full px-2.5 py-1 text-xs font-medium text-foreground flex items-center gap-1">
          {tattoo.freshPhotos.length}
          <Image className="w-3 h-3" />
        </div>

        {/* Date pill - Bottom Center */}
        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-background/90 backdrop-blur-sm rounded-full px-2.5 py-1 text-xs font-medium text-foreground">
          {format(tattoo.createdAt, 'd.MM.yyyy')}
        </div>

        {/* Add healed button for clients */}
        {!isArtist && !hasHealed && onAddHealed && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onAddHealed()
            }}
            className="absolute bottom-2 left-2 bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1 hover:bg-primary/90 transition-colors"
          >
            <Plus className="w-3 h-3" />
            Healed
          </button>
        )}
      </div>

      {/* Date below card - removed, now as pill on top left */}
    </div>
  )
}

interface AddTattooCardProps {
  onClick: () => void
  hideDate?: boolean
}

export function AddTattooCard({ onClick, hideDate = false }: AddTattooCardProps) {
  return (
    <div className="flex flex-col items-center flex-shrink-0">
      {/* Subtle placeholder card */}
      <div
        className="rounded-[var(--radius)] bg-muted/30 border border-border/30 flex flex-col items-center justify-center cursor-pointer hover:bg-muted/50 hover:border-border/50 transition-all duration-300 gap-2 px-4"
        style={{ width: '160px', height: '200px' }}
        onClick={onClick}
      >
        <Plus className="w-6 h-6 text-muted-foreground/50 hover:text-muted-foreground/70 transition-colors" />
        <p className="text-xs text-muted-foreground/60 text-center leading-tight hover:text-muted-foreground/80 transition-colors">
          New projects will show up here!
        </p>
      </div>

      {/* Empty date space for alignment */}
      {!hideDate && (
        <div className="h-5 mt-3" />
      )}
    </div>
  )
}

